import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useSplashBentoStats } from "@/hooks/useSplashBentoStats";
import ultureLogo from "@/assets/ulture-logo-transparent.png";
import { ClipboardList, Bug, AlarmClock } from "lucide-react";
import type { N8nWeatherCurrent, N8nWeatherDay } from "@/lib/weather";
import { weatherIcon, weatherLabel } from "@/lib/weatherIcons";

/**
 * Branded "Welcome back, {name}" cold-start splash screen.
 *
 * Visual only — never affects business logic, auth, or routing.
 * Designed to be shown by <SplashGate> for ~2.8s on first session mount.
 *
 * Name resolution:
 *   - worker  -> first name from profile.full_name
 *   - other   -> company.name
 *   - else    -> just "Welcome back"
 *
 * Below the greeting, a glassmorphism bento card displays 3 mini-stats:
 *   - Today's weather (temp + condition)
 *   - Tasks scheduled today
 *   - Active pest scouting in last 30 days
 *
 * The bento is fully defensive — if any data is missing the cell shows "—"
 * so the splash never crashes nor blocks the cold-start animation.
 */

/* El icono y el texto salían de una heurística propia (mm de lluvia, humedad y una
   potencia solar inventada), distinta de la del dashboard: las dos pantallas podían
   contradecirse. Ahora las dos leen `weatherIcons.ts`, que sólo traduce el código de
   condición que manda la API. */

interface WelcomeBackScreenProps {
  /** Called when the user taps the "Start" button. */
  onStart?: () => void;
  /** Duration of the deterministic progress bar, in ms. Default 2100. */
  progressDurationMs?: number;
}

export function WelcomeBackScreen({
  onStart,
  progressDurationMs = 2100,
}: WelcomeBackScreenProps = {}) {
  const { t } = useTranslation();

  // Read auth defensively — this component may render BEFORE AuthProvider mounts.
  // Always prefer the user's own first name, regardless of role.
  // Fall back to company name only if no profile name is available.
  let displayName: string | null = null;
  try {
    const { profile, company } = useAuth();
    if (profile?.full_name) {
      displayName = profile.full_name.trim().split(/\s+/)[0] ?? null;
    } else if (company?.name) {
      displayName = company.name;
    }
  } catch {
    displayName = null;
  }

  // Bento stats — fully defensive, never throws.
  let weatherToday: N8nWeatherDay | null = null;
  let weatherCurrent: N8nWeatherCurrent | null = null;
  let tasksToday: number | null = null;
  let activePests: number | null = null;
  let overdueTasks: number | null = null;
  let isWorker = false;
  try {
    const stats = useSplashBentoStats();
    weatherToday = stats.weatherToday;
    weatherCurrent = stats.weatherCurrent;
    tasksToday = stats.tasksToday;
    activePests = stats.activePests;
    overdueTasks = stats.overdueTasks;
    isWorker = stats.isWorker;
  } catch {
    // Auth/Query unavailable — bento simply shows "—" placeholders.
  }

  const heroCondition = weatherCurrent?.condition ?? weatherToday?.condition ?? null;
  const WeatherIcon = weatherIcon(heroCondition);
  // La temperatura de ahora. Antes se enseñaba `avg_temp_C`, que es la de las 12:00.
  const heroTemp = weatherCurrent?.temp ?? weatherToday?.avg_temp_C ?? null;
  const tempLabel = heroTemp !== null ? `${Math.round(heroTemp)}°` : "—";
  const conditionLabel = heroCondition ? weatherLabel(heroCondition, "—") : "—";
  const tasksLabel = tasksToday !== null ? String(tasksToday) : "—";
  // Tercera celda: al operario le sirven sus tareas atrasadas; a los demás perfiles,
  // las plagas activas de la empresa.
  const thirdValue = isWorker ? overdueTasks : activePests;
  const thirdLabel = thirdValue !== null ? String(thirdValue) : "—";
  // Iconos de línea como el del tiempo, en vez de emojis (cada sistema los pinta
  // a su manera y rompían la armonía de la tarjeta).
  const ThirdIcon = isWorker ? AlarmClock : Bug;
  const thirdCaption = isWorker
    ? t("welcomeScreen.bento.overdueTasks", "Overdue")
    : t("welcomeScreen.bento.activePests", "Active pests");

  // Once the progress bar finishes, swap it for the "Start" button.
  const [progressDone, setProgressDone] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setProgressDone(true), progressDurationMs);
    return () => clearTimeout(timer);
  }, [progressDurationMs]);

  return (
    <div
      className="fixed inset-0 w-screen h-[100dvh] flex flex-col items-center justify-center gap-6 px-6 overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, #7DE3DD 0%, #5BB8E8 55%, #4A9FE0 100%)",
        paddingTop: "var(--safe-area-top, env(safe-area-inset-top, 0px))",
        paddingBottom: "var(--safe-bottom)",
      }}

      role="status"
      aria-live="polite"
    >
      {/* Soft ambient blobs — depth without lag (pure CSS, GPU-accelerated) */}
      <div
        className="pointer-events-none absolute -top-24 -left-20 w-72 h-72 rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, #FFFFFF 0%, transparent 70%)" }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-32 -right-16 w-96 h-96 rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, #B8F3EF 0%, transparent 70%)" }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute top-1/3 right-1/4 w-40 h-40 rounded-full opacity-20 blur-2xl"
        style={{ background: "radial-gradient(circle, #FFFFFF 0%, transparent 70%)" }}
        aria-hidden="true"
      />

      {/* App-icon style logo card */}
      <div
        className="relative w-28 h-28 rounded-[28px] bg-white flex items-center justify-center animate-scale-in"
        style={{ boxShadow: "0 25px 70px rgba(43, 124, 219, 0.35), 0 8px 20px rgba(0,0,0,0.12)" }}
      >
        <img
          src={ultureLogo}
          alt=""
          className="w-24 h-24 object-contain select-none"
          draggable={false}
        />
      </div>

      {/* Greeting */}
      <div
        className="relative text-center text-white animate-fade-in"
        style={{ animationDelay: "200ms", animationFillMode: "both" }}
      >
        <h1
          className="text-3xl md:text-4xl font-bold tracking-tight"
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.12)" }}
        >
          {t("welcomeScreen.welcomeBack", "Welcome back")}
          {displayName ? "," : ""}
        </h1>
        {displayName && (
          <p
            className="text-xl md:text-2xl font-semibold mt-1.5 text-white animate-fade-in"
            style={{
              animationDelay: "350ms",
              animationFillMode: "both",
              textShadow: "0 2px 12px rgba(0,0,0,0.15)",
            }}
          >
            {displayName}
          </p>
        )}
      </div>

      {/* Bento card — 3 mini-stats (weather, tasks, pests) */}
      <div
        className="relative z-10 w-full max-w-[340px] animate-fade-in"
        style={{ animationDelay: "550ms", animationFillMode: "both" }}
      >
        <div
          className="grid grid-cols-3 rounded-3xl px-2 py-3.5 border"
          style={{
            background: "rgba(255, 255, 255, 0.18)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderColor: "rgba(255, 255, 255, 0.35)",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
          }}
        >
          {/* Weather */}
          <div className="flex flex-col items-center justify-center px-1 py-1">
            <WeatherIcon className="w-6 h-6 text-white" strokeWidth={2.2} />
            <div
              className="text-xl font-extrabold text-white mt-2 leading-none"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.15)" }}
            >
              {tempLabel}
            </div>
            <div className="text-[10px] font-medium text-white/85 mt-1 truncate max-w-full text-center">
              {conditionLabel}
            </div>
          </div>

          {/* Tasks today */}
          <div
            className="flex flex-col items-center justify-center px-1 py-1 border-l border-r"
            style={{ borderColor: "rgba(255, 255, 255, 0.3)" }}
          >
            <ClipboardList className="w-6 h-6 text-white/90" strokeWidth={2.2} />
            <div
              className="text-xl font-extrabold text-white mt-2 leading-none"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.15)" }}
            >
              {tasksLabel}
            </div>
            <div className="text-[10px] font-medium text-white/85 mt-1 text-center">
              {t("welcomeScreen.bento.tasksToday", "Tasks today")}
            </div>
          </div>

          {/* Tareas atrasadas (operarios) / plagas activas (el resto) */}
          <div className="flex flex-col items-center justify-center px-1 py-1">
            <ThirdIcon className="w-6 h-6 text-white/90" strokeWidth={2.2} />
            <div
              className="text-xl font-extrabold text-white mt-2 leading-none"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.15)" }}
            >
              {thirdLabel}
            </div>
            <div className="text-[10px] font-medium text-white/85 mt-1 text-center">
              {thirdCaption}
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar → Start button swap. The progress bar fills smoothly
          over `progressDurationMs`, then a soft "Start" pill replaces it. */}
      <div className="relative h-10 w-[180px] flex items-center justify-center">
        {!progressDone ? (
          <div className="relative h-[3px] w-full bg-white/25 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full animate-splash-progress" />
          </div>
        ) : (
          <button
            type="button"
            onClick={onStart}
            className="px-8 py-2 rounded-full bg-white text-[#4A9FE0] text-sm font-semibold shadow-lg shadow-black/15 active:scale-[0.97] transition-transform animate-fade-in"
            style={{ animationDuration: "300ms", animationFillMode: "both" }}
          >
            {t("welcomeScreen.start", "Start")}
          </button>
        )}
      </div>
    </div>
  );
}

export default WelcomeBackScreen;
